/**
 * InfinitSales - chatCache (frente C4, flag `chat_cache`)
 *
 * LRU de 20 conversas por sessao, chaveado por canal + id da conversa, com
 * { messages, chatState, labels, assignment, fetchedAt }. Abrir uma conversa
 * ja vista pinta na hora com o que esta aqui e revalida por baixo; reabrir
 * custava 8 requisicoes (medido em 22/09).
 *
 * Regras:
 *  - `messages` guarda a lista JA NORMALIZADA do jeito que o ChatWindow do
 *    canal renderiza (ordem crescente). Quem grava e o ChatWindow (espelho do
 *    estado) ou o prefetch do shell (mouseenter), com o mesmo normalizador.
 *  - NEW_MESSAGE de uma conversa em cache aplica o patch (append com dedup),
 *    nao descarta a entrada.
 *  - Prefetch dedup em voo por chave; so no desktop (pointer: fine) e com
 *    debounce de 150 ms no shell.
 *  - Limpo no logout (authService.logout chama clearChatCache).
 */

const MAX_ENTRIES = 20;
export const CHAT_CACHE_FRESH_MS = 60_000;
export const CHAT_STATE_FRESH_MS = 60_000;

// Map preserva ordem de insercao: o mais antigo fica no inicio.
const _entries = new Map();
const _inflight = new Map();

export function chatCacheKey(channel, id) {
  if (!channel || id === undefined || id === null || id === '') return null;
  return `${channel}:${String(id)}`;
}

function touch(key) {
  const e = _entries.get(key);
  if (!e) return null;
  _entries.delete(key);
  _entries.set(key, e);
  return e;
}

export function getChatEntry(channel, id) {
  const key = chatCacheKey(channel, id);
  if (!key) return null;
  return touch(key);
}

export function hasChatMessages(channel, id) {
  const e = getChatEntry(channel, id);
  return Boolean(e && Array.isArray(e.messages));
}

/** Merge parcial na entrada (cria se nao existir) e move para o fim (LRU). */
export function setChatEntry(channel, id, patch) {
  const key = chatCacheKey(channel, id);
  if (!key) return null;
  const prev = _entries.get(key) || {
    messages: null, chatState: null, labels: null, assignment: null, fetchedAt: 0,
  };
  _entries.delete(key);
  const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
  _entries.set(key, next);
  while (_entries.size > MAX_ENTRIES) {
    const oldest = _entries.keys().next().value;
    _entries.delete(oldest);
  }
  return next;
}

export function setChatMessages(channel, id, messages) {
  return setChatEntry(channel, id, {
    messages: Array.isArray(messages) ? messages : [],
    fetchedAt: Date.now(),
  });
}

/**
 * NEW_MESSAGE de uma conversa que esta no cache mas nao esta aberta: anexa a
 * mensagem (dedup por `idOf`) em vez de jogar a entrada fora. Se a conversa
 * nao esta no cache, nao faz nada (nao ha o que remendar).
 */
export function appendChatMessage(channel, id, message, idOf) {
  const key = chatCacheKey(channel, id);
  if (!key || !message) return;
  const e = _entries.get(key);
  if (!e || !Array.isArray(e.messages)) return;
  const ident = typeof idOf === 'function' ? idOf : (m) => m && (m.id || m.mid || m.wamid || (m.key && m.key.id));
  const novoId = ident(message);
  if (novoId && e.messages.some((m) => ident(m) === novoId)) return;
  setChatEntry(channel, id, { messages: [...e.messages, message] });
}

/** Aplica um updater na lista em cache (ex.: status de entrega, reacao). */
export function patchChatMessages(channel, id, updater) {
  const key = chatCacheKey(channel, id);
  if (!key) return;
  const e = _entries.get(key);
  if (!e || !Array.isArray(e.messages)) return;
  const next = updater(e.messages);
  if (next && next !== e.messages) setChatEntry(channel, id, { messages: next });
}

export function invalidateChat(channel, id) {
  const key = chatCacheKey(channel, id);
  if (key) { _entries.delete(key); _inflight.delete(key); }
}

export function clearChatCache() {
  _entries.clear();
  _inflight.clear();
}

/**
 * Prefetch das mensagens (mouseenter na linha). `fetcher` devolve a lista ja
 * normalizada. Dedup em voo e pula quando a entrada ainda esta fresca.
 */
export function prefetchChatMessages(channel, id, fetcher) {
  const key = chatCacheKey(channel, id);
  if (!key || typeof fetcher !== 'function') return Promise.resolve(null);
  const e = _entries.get(key);
  if (e && Array.isArray(e.messages) && Date.now() - e.fetchedAt < CHAT_CACHE_FRESH_MS) {
    return Promise.resolve(e.messages);
  }
  if (_inflight.has(key)) return _inflight.get(key);
  const p = Promise.resolve()
    .then(fetcher)
    .then((messages) => {
      if (Array.isArray(messages)) setChatMessages(channel, id, messages);
      return messages;
    })
    .catch(() => null)
    .finally(() => { _inflight.delete(key); });
  _inflight.set(key, p);
  return p;
}

/** Desktop com mouse de verdade: prefetch por hover so faz sentido aqui. */
export function hoverPrefetchAllowed() {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: fine)').matches;
  } catch {
    return false;
  }
}

/**
 * Agenda um prefetch com debounce de 150 ms. Um unico timer por shell: passar
 * o mouse por 10 linhas em 1 s dispara so a ultima.
 */
export function createHoverPrefetcher(delay = 150) {
  let timer = null;
  return {
    schedule(fn) {
      if (!hoverPrefetchAllowed()) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; try { fn(); } catch { /* best-effort */ } }, delay);
    },
    cancel() {
      if (timer) { clearTimeout(timer); timer = null; }
    },
  };
}

const chatCache = {
  chatCacheKey,
  getChatEntry,
  hasChatMessages,
  setChatEntry,
  setChatMessages,
  appendChatMessage,
  patchChatMessages,
  invalidateChat,
  clearChatCache,
  prefetchChatMessages,
  hoverPrefetchAllowed,
  createHoverPrefetcher,
};

export default chatCache;
